import {
  NgModule,
  ModuleWithProviders,
  Type,
  Injectable,
  Provider
} from '@angular/core';
import { DxFormsRootModule } from './forms-root.module';
import {
  ROOT_FORMS,
  FEATURE_FORMS
} from './tokens';
import { FormsRunner } from './forms-runner';
import { DxFormGroup } from './form-group';
import { AbstractFormDefinition } from './abstract-form-definition';
import {
  FORM_DEFINITION_METADATA_KEY,
  FORM_INSTANCE_METADATA_KEY
} from './decorators/metadata-keys';
import { FormInstanceDefinition } from './decorators/form-instance';
import { DxFormsFeatureModule } from './forms-feature.module';
import {
  getMetadata,
  setMetadata
} from '@digitaix/helpers';

@NgModule({})
export class DxFormsModuleDefinition {
  public static forRoot(
    formDefinitionClasses: Array<Type<AbstractFormDefinition<any>>> = [],
  ): ModuleWithProviders<DxFormsRootModule> {
    return {
      ngModule: DxFormsRootModule,
      providers: [
        FormsRunner,
        formDefinitionClasses,
        {
          provide: ROOT_FORMS,
          deps: formDefinitionClasses,
          useFactory: createFormDefinitionInstancesFactory,
        }
      ]
    };
  }

  public static forFeature(
    formDefinitionClasses: Array<Type<AbstractFormDefinition<any>>>,
  ): ModuleWithProviders<DxFormsFeatureModule> {
    return {
      ngModule: DxFormsFeatureModule,
      providers: [
        formDefinitionClasses,
        {
          provide: FEATURE_FORMS,
          multi: true,
          deps: formDefinitionClasses,
          useFactory: createFormDefinitionInstancesFactory,
        },
      ]
    }
  }

}

export interface DxFormDefinitionWithMeta {
  group: Type<DxFormGroup<any>>;
  instances?: FormInstanceDefinition<any, any>[];
  instance?: FormInstanceDefinition<any, any>;
  formDefinition: Type<AbstractFormDefinition<any>>;
  multi?: boolean;
}

export function getFormDefinitionsWithInstance(rootFormDefinitions: Array<Type<AbstractFormDefinition<any>>>): Array<DxFormDefinitionWithMeta> {
  return rootFormDefinitions.map(formDefinition => ({
    group: getMetadata<Type<DxFormGroup<any>>>(FORM_DEFINITION_METADATA_KEY, formDefinition),
    instances: getMetadata<FormInstanceDefinition<any, any>[]>(FORM_INSTANCE_METADATA_KEY, formDefinition) || [{}] as any,
    formDefinition,
  })).map((withMeta: any) => withMeta.instances.map(instance => ({
    group: withMeta.group,
    instance,
    multi: withMeta.instances.length > 1,
    formDefinition: withMeta.formDefinition
  }))).reduce((a, b) => [ ...a, ...b ], []).map(withMeta => {

    if (withMeta.multi) {
      @Injectable()
        // tslint:disable-next-line:class-name
      class formDefinition extends withMeta.formDefinition {}

      setMetadata(FORM_INSTANCE_METADATA_KEY, withMeta.instance, formDefinition);

      return { ...withMeta, formDefinition };

    } else {
      return withMeta;
    }

  });
}

export function getFormDefinitionClasses(rootFormDefinitions: Array<Type<AbstractFormDefinition<any>>>): Array<Type<AbstractFormDefinition<any>>> {
  const formDefinitionsWithInstance = getFormDefinitionsWithInstance(rootFormDefinitions);
  return formDefinitionsWithInstance.map(fdwi => fdwi.formDefinition);
}

export function filterFormDefinitions(rootFormDefinitions: Array<Type<AbstractFormDefinition<any>>>): Provider[] {
  const formDefinitionsWithInstance = getFormDefinitionsWithInstance(rootFormDefinitions);
  return formDefinitionsWithInstance
    .filter(fdwi => fdwi.multi && fdwi.instance.token)
    .map(fdwi => ({
      provide: fdwi.instance.token,
      useExisting: fdwi.formDefinition,
    }))
}

export function getFormDefinitionProviders(...rootFormDefinitions: Array<Type<AbstractFormDefinition<any>>>): [Array<Type<AbstractFormDefinition<any>>>, Provider[]] {

  const formDefinitionsWithInstance = getFormDefinitionsWithInstance(rootFormDefinitions);

  const formDefinitions = formDefinitionsWithInstance
    .filter(fdwi => fdwi.multi && fdwi.instance.token)
    .map(fdwi => ({
      provide: fdwi.instance.token,
      useExisting: fdwi.formDefinition,
    }));

  const formDefinitionClasses = formDefinitionsWithInstance.map(fdwi => fdwi.formDefinition);

  return [ formDefinitionClasses, formDefinitions ];

}

export function createFormDefinitionInstancesFactory(
  ...instances: AbstractFormDefinition<any, any>[]
) {

  // TODO : fix aot issue
  // for (const instance of instances) {
  //
  //   const instanceDefinition = getMetadata<FormInstanceDefinition<any, any>>(FORM_INSTANCE_METADATA_KEY, instance.constructor);
  //
  //   if (instanceDefinition) {
  //
  //     if (instanceDefinition.collectionPath) {
  //       instance.collectionPath = instanceDefinition.collectionPath;
  //     }
  //     if (instanceDefinition.editComponent) {
  //       instance.editComponent = instanceDefinition.editComponent;
  //     }
  //     if (instanceDefinition.formId) {
  //       instance.formId = instanceDefinition.formId;
  //     }
  //     if (instanceDefinition.load) {
  //       instance.load = instanceDefinition.load;
  //     }
  //     if (instanceDefinition.title) {
  //       instance.title = instanceDefinition.title;
  //     }
  //
  //   }
  //
  // }

  return instances;

}
